import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  IconButton,
  Divider,
  Link,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link as RouterLink } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Order Now', path: '/order-now' },
    { name: 'Track Order', path: '/track-order' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Custom Card Designs',
    'Nikahanam Premium Nikhanama',
    'Nikah Pen',
    'Nikah Frame',
    'Nikah Mirror',
    'Bulk Orders'
  ];

  const socialLinks = [
    { icon: <InstagramIcon />, href: 'https://www.instagram.com/ishqnama_art/', label: 'Instagram' },
    { icon: <WhatsAppIcon />, href: 'https://wa.me/916393342414', label: 'WhatsApp' },
  ];

  return (
    <Box
      ref={ref}
      sx={{
        background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255, 107, 157, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(108, 99, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Box sx={{ py: 6 }}>
            <Grid container spacing={4}>
              {/* Company Info */}
              <Grid item xs={12} md={4}>
                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        component="img"
                        src="/pink_logo.png"
                        alt="Ishqnama Art Logo"
                        sx={{
                          height: 40,
                          mr: 1,
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        lineHeight: 1.6,
                        mb: 3,
                      }}
                    >
                      Making your special moments unforgettable with beautiful handcrafted 
                      decorations and personalized gifts. We bring creativity and elegance 
                      to every celebration.
                    </Typography>
                  </Box>

                  {/* Contact Info */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon sx={{ mr: 2, color: '#FF6B9D' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        +91 6393342414
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailIcon sx={{ mr: 2, color: '#FF6B9D' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        ishqnamaart@gmail.com
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon sx={{ mr: 2, color: '#FF6B9D' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        Bangalore, India
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>

              {/* Quick Links */}
              <Grid item xs={12} sm={6} md={2}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#FF6B9D',
                    }}
                  >
                    Quick Links
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {quickLinks.map((link, index) => (
                      <Link
                        key={index}
                        component={RouterLink}
                        to={link.path}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          textDecoration: 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#FF6B9D',
                            transform: 'translateX(5px)',
                          },
                        }}
                      >
                        {link.name}
                      </Link>
                    ))}
                    <Link
                      component={RouterLink}
                      to="/login"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#FF6B9D',
                          transform: 'translateX(5px)',
                        },
                      }}
                    >
                      Dashboard
                    </Link>
                  </Box>
                </motion.div>
              </Grid>

              {/* Services */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#FF6B9D',
                    }}
                  >
                    Our Services
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {services.map((service, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#FF6B9D',
                            transform: 'translateX(5px)',
                          },
                        }}
                      >
                        {service}
                      </Typography>
                    ))}
                  </Box>
                </motion.div>
              </Grid>

              {/* Newsletter & Social */}
              <Grid item xs={12} md={3}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#FF6B9D',
                    }}
                  >
                    Connect With Us
                  </Typography>
                  
                  {/* Social Links */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {socialLinks.map((social, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <IconButton
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #FF6B9D 0%, #E91E63 100%)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Bottom Section */}
          <motion.div variants={itemVariants}>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 3 }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 3,
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                © {new Date().getFullYear()} Ishqnama Art. All rights reserved.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Link
                  href="#"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: '#FF6B9D' },
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="#"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: '#FF6B9D' },
                  }}
                >
                  Terms of Service
                </Link>
                <Link
                  href="#"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: '#FF6B9D' },
                  }}
                >
                  Cookie Policy
                </Link>
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer; 